import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, Mail } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const TEAL = "#14B8A6";
const BLACK = "#111111";
const NAME = "Beyond Smile Dental";
const FB = "https://www.facebook.com/Dr.SamiraRodriguezTehrani/";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

export default function BeyondSmileConcept() {
  const [open, setOpen] = useState(false);
  const links = [["Services", "#services"], ["Technology", "#tech"], ["Dentist", "#dentist"], ["Contact", "#visit"]];

  return (
    <div id="top" className="min-h-screen bg-white" style={{ color: "#111111", fontFamily: "'Space Grotesk', sans-serif" }} data-testid="beyond-smile-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');`}</style>

      <div data-testid="dental-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white border-b border-black/10">
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
          <a href="#top" data-testid="bsd-logo" className="font-bold text-lg tracking-tight uppercase">Beyond<span style={{ color: TEAL }}>Smile</span></a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm uppercase tracking-wide text-black/60 hover:text-black transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="bsd-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-semibold text-black transition-transform hover:-translate-y-0.5" style={{ background: TEAL }}>Message Us</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="bsd-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — asymmetric split, no giant tooth-decay graphic */}
      <section className="grid md:grid-cols-2 min-h-[80vh]" data-testid="bsd-hero">
        <div className="flex flex-col justify-center px-8 sm:px-14 py-16" style={{ background: BLACK }}>
          <motion.div {...fadeUp}>
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: TEAL }}>Central Arcade · Dumaguete</span>
            <h1 className="mt-6 text-5xl sm:text-6xl font-bold text-white leading-[1.05]">Modern dentistry,<br />done right.</h1>
            <p className="mt-6 text-white/60 max-w-md">Beyond Smile Dental focuses on precise orthodontic and cosmetic work, backed by digital diagnostics — with Dr. Samira Sadat Rodriguez Tehrani leading every case.</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#visit" data-testid="bsd-hero-cta" className="px-7 py-3.5 text-sm font-semibold text-black" style={{ background: TEAL }}>Book an Appointment</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="bsd-hero-contact" className="px-7 py-3.5 text-sm font-semibold text-white border border-white/30">Contact Us</a>
            </div>
            <p className="mt-8 text-sm text-white/50">5.0★ Rating · 22 Reviews</p>
          </motion.div>
        </div>
        <motion.div {...fadeUp} className="relative min-h-[320px]">
          <img src="/dental/beyond-smile-dental-concept/banner.jpg" alt="A real orthodontic treatment result shared by Beyond Smile Dental" className="absolute inset-0 w-full h-full object-cover" />
        </motion.div>
      </section>

      {/* SERVICES — asymmetric 2x2, not icon cards */}
      <section id="services" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <motion.h2 {...fadeUp} className="text-4xl font-bold mb-14">Our Focus</motion.h2>
        <div className="grid md:grid-cols-2 gap-1 border border-black/10">
          {[
            { n: "01", t: "Orthodontics", d: "Braces and bite-correction treatment planned around each patient's case." },
            { n: "02", t: "Cosmetic Dentistry", d: "Treatments focused on the health and appearance of your smile." },
            { n: "03", t: "General Dentistry", d: "Routine care, check-ups and prevention for the whole family." },
            { n: "04", t: "Restorations", d: "Repairing and rebuilding teeth to last, without compromising on comfort." },
          ].map((s) => (
            <motion.div key={s.n} {...fadeUp} className="p-10 border border-black/10">
              <span className="text-xs font-bold" style={{ color: TEAL }}>{s.n}</span>
              <h3 className="mt-3 text-2xl font-semibold">{s.t}</h3>
              <p className="mt-3 text-black/60">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TECHNOLOGY */}
      <section id="tech" className="py-24" style={{ background: BLACK }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp} className="order-2 md:order-1">
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: TEAL }}>In-House Technology</span>
            <h2 className="mt-4 text-4xl font-bold text-white">Digital Panoramic X-Ray</h2>
            <p className="mt-5 text-white/60 leading-relaxed">We use digital panoramic X-ray imaging on-site, so treatment planning is faster and more precise — without sending patients elsewhere for diagnostics.</p>
          </motion.div>
          <motion.div {...fadeUp} className="order-1 md:order-2">
            <img src="/dental/beyond-smile-dental-concept/xray.jpg" alt="A real digital panoramic X-ray used at Beyond Smile Dental" className="w-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* COSMETIC RESULT */}
      <section id="dentist" className="max-w-6xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-[1.1fr,1fr] gap-12 items-center">
        <motion.div {...fadeUp}>
          <img src="/dental/beyond-smile-dental-concept/veneer-result.jpg" alt="A real cosmetic veneer result shared by Beyond Smile Dental" className="w-full object-cover" />
        </motion.div>
        <motion.div {...fadeUp}>
          <span className="text-xs uppercase tracking-[0.3em]" style={{ color: TEAL }}>Your Dentist</span>
          <h2 className="mt-3 text-3xl font-bold">Dr. Samira Sadat Rodriguez Tehrani</h2>
          <p className="mt-4 text-black/60">Leads every case at Beyond Smile Dental — recommended locally for general and orthodontic care at the Central Arcade clinic.</p>
        </motion.div>
      </section>

      {/* PATIENT MENTIONS */}
      <section className="py-20" style={{ background: "#F7F7F7" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <motion.span {...fadeUp} className="text-xs uppercase tracking-[0.3em]" style={{ color: TEAL }}>As shared by patients online</motion.span>
          <div className="mt-8 grid sm:grid-cols-2 gap-8 text-left">
            <motion.p {...fadeUp} className="text-black/70 italic">"Recommends Beyond Smile Dental at Central Arcade" — r/dumaguete</motion.p>
            <motion.p {...fadeUp} className="text-black/70 italic">"Recommends trying Beyond Smile for orthodontic care" — r/dumaguete</motion.p>
          </div>
        </div>
      </section>

      {/* CTA + VISIT */}
      <section id="visit" className="py-24" style={{ background: BLACK }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl font-bold text-white">Book an Appointment</h2>
            <ul className="mt-8 space-y-5 text-white/70">
              <li className="flex items-start gap-4"><MapPin size={20} style={{ color: TEAL }} className="mt-0.5" />B6 Central Arcade, JP Teves St., Dumaguete City 6200</li>
              <li className="flex items-start gap-4"><Phone size={20} style={{ color: TEAL }} className="mt-0.5" /><a href="tel:+639178811737" className="hover:underline">+63 917 881 1737</a></li>
              <li className="flex items-start gap-4"><Mail size={20} style={{ color: TEAL }} className="mt-0.5" /><a href="mailto:beyondsmiledental@gmail.com" className="hover:underline">beyondsmiledental@gmail.com</a></li>
              <li className="flex items-start gap-4"><Facebook size={20} style={{ color: TEAL }} className="mt-0.5" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="bsd-facebook-link" className="hover:underline">Dr.SamiraRodriguezTehrani</a></li>
            </ul>
            <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="bsd-cta-message" className="inline-block mt-9 px-8 py-3.5 text-sm font-semibold text-black" style={{ background: TEAL }}>Message on Facebook</a>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[320px]">
            <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("Central Arcade JP Teves St Dumaguete City")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </motion.div>
        </div>
      </section>

      <footer className="py-10 bg-white border-t border-black/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-black/50">
          <p className="font-bold text-black">Beyond Smile Dental</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-semibold text-black shadow-xl" style={{ background: TEAL }}>Message Beyond Smile</a>
      <ConceptCTAPill slug="beyond-smile-dental-concept" />
    </div>
  );
}
